import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { useData } from "@/context/DataContext";
import { useStats } from "@/hooks/useStats";
import { cashFor } from "@/data/cash";
import { money } from "@/lib/money";
import { t } from "@/lib/i18n";

function Bar({ title, amount, pct, sub, has, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between text-sm"><span>{title}</span><b className="num">{money(amount)}</b></div>
      <div className="mm-bar mt-2"><motion.i initial={{ width: 0 }} animate={{ width: `${Math.min(100, pct)}%` }} transition={{ duration: 0.8 }} style={{ background: pct > 100 ? "var(--dg)" : "linear-gradient(90deg,var(--g1),var(--g2))" }} /></div>
      <div className="mt-1 text-xs text-[var(--mu)]">{has ? `${pct.toFixed(1)}% ${sub}` : t("Add income to calculate the percentage")}</div>
    </div>
  );
}

export function AverageSpending() {
  const st = useStats();
  const now = new Date(), td = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const cur = td >= st.s && td < st.e;
  const end = cur ? new Date(td.getTime() + 864e5) : st.e;
  const days = Math.max(1, Math.ceil((Math.min(end, st.e) - st.s) / 864e5));
  const daily = st.total / days, di = st.inP / days;
  return (
    <section className="mm-card mm-rise space-y-5" data-testid="average-spending-card">
      <h2 className="mm-h2 text-lg">{t("Average spending")}</h2>
      <Bar title={t("Average daily spending")} amount={daily} pct={di > 0 ? (daily / di) * 100 : 0} sub={t("of average daily income")} has={st.inP > 0} testId="avg-daily" />
      <Bar title={t("Spending in selected period")} amount={st.total} pct={st.inP > 0 ? (st.total / st.inP) * 100 : 0} sub={t("of income for this timeframe")} has={st.inP > 0} testId="avg-period" />
    </section>
  );
}

function Cash({ d, n, i }) {
  const [, label, kind, color, img] = d;
  return (
    <motion.div initial={{ opacity: 0, y: 10, rotate: -4 }} animate={{ opacity: 1, y: 0, rotate: 0 }} transition={{ delay: i * 0.05 }} className="flex items-center gap-2" data-testid={`cash-${label}`}>
      {img ? <span className="flex min-h-14 min-w-24 items-center justify-center rounded-lg bg-black/10 p-1.5 sm:min-h-16 sm:min-w-28"><img src={img} alt={`${label} note`} loading="lazy" className="h-auto max-h-12 w-auto max-w-[104px] rounded-[5px] object-contain shadow-[0_3px_10px_rgba(0,0,0,.25)] transition-transform hover:z-10 hover:scale-125 sm:max-h-14" /></span>
        : kind === "note" ? <div className="mm-note" style={{ background: `linear-gradient(135deg, ${color}, color-mix(in srgb, ${color} 60%, #000))` }}>{label}</div>
          : <div className="mm-coin" style={{ background: `radial-gradient(circle at 35% 30%, #fff8, ${color})` }}>{label}</div>}
      <span className="num text-sm font-bold">×{n}</span>
    </motion.div>
  );
}

export function CashBreakdown() {
  const { db } = useData();
  const st = useStats();
  const den = cashFor(db.settings.prefs.currency);
  const minUnit = den[den.length - 1][0];
  let rem = Math.round((st.total * 100) / minUnit) * minUnit, nN = 0, nC = 0;
  const shown = rem / 100;
  const cu = den.map((d) => { const n = Math.floor(rem / d[0]); rem -= n * d[0]; if (d[2] === "note") nN += n; else nC += n; return { d, n }; }).filter((x) => x.n);
  return (
    <section className="mm-card mm-rise" data-testid="aud-cash-breakdown-widget">
      <h2 className="mm-h2 mb-1 text-lg">{t("Cash breakdown")}</h2>
      {!cu.length ? <p className="text-sm text-[var(--mu)]">{t("Nothing spent in this period yet.")}</p> : (
        <>
          <p className="mb-4 text-xs text-[var(--mu)]" data-testid="cash-summary">{nN} {t("notes")} · {nC} {t("coins")} · {money(shown)}</p>
          <div className="flex flex-wrap gap-3">{cu.map(({ d, n }, i) => <Cash key={d[0]} d={d} n={n} i={i} />)}</div>
        </>
      )}
    </section>
  );
}

export function Unnecessary() {
  const { db } = useData();
  const st = useStats();
  const un = db.settings.categories.filter((c) => c.u);
  const tot = un.reduce((a, c) => a + (st.byCat[c.id] || 0), 0);
  const pct = st.total > 0 ? (tot / st.total) * 100 : 0;
  return (
    <section className="mm-card mm-rise" style={{ background: `color-mix(in srgb, var(--unn) ${tot > 0 ? 8 : 0}%, var(--card))` }} data-testid="unnecessary-spending-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--mu)]">{t("Optional spending")}</div>
          <div className="num mt-1 text-3xl font-bold" style={{ color: tot > 0 ? "var(--unn)" : "inherit" }} data-testid="unnecessary-total">{money(tot)}</div>
        </div>
        <AlertTriangle className="h-6 w-6 text-[var(--unn)] opacity-70" />
      </div>
      <div className="mt-2 text-sm text-[var(--mu)]">{pct.toFixed(0)}% {t("of total")}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">{un.filter((c) => st.byCat[c.id] > 0).map((c) => <span key={c.id} className="mm-chip !cursor-default text-xs">{c.e} <span className="num">{money(st.byCat[c.id] || 0)}</span></span>)}</div>
    </section>
  );
}
